import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import API_URL from "../../config/ApiUrl";

// One thunk per dashboard widget/section so each loads independently.
// Each unwraps the ApiBaseResponse envelope and returns its `data` payload.
const makeThunk = (name, url) =>
  createAsyncThunk(`dashboard/${name}`, async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(url, payload);
      return data?.data ?? null;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to load",
      );
    }
  });

export const getKpis = makeThunk("kpis", API_URL.DASHBOARD.KPIS);
export const getSalesOrderSummary = makeThunk(
  "salesOrderSummary",
  API_URL.DASHBOARD.SALES_ORDER_SUMMARY,
);
export const getFunnel = makeThunk("funnel", API_URL.DASHBOARD.FUNNEL);
export const getTopSellingItems = makeThunk(
  "topSellingItems",
  API_URL.DASHBOARD.TOP_SELLING_ITEMS,
);
export const getTopLocations = makeThunk(
  "topLocations",
  API_URL.DASHBOARD.TOP_LOCATIONS,
);
export const getTopCustomers = makeThunk(
  "topCustomers",
  API_URL.DASHBOARD.TOP_CUSTOMERS,
);
export const getTopVendors = makeThunk(
  "topVendors",
  API_URL.DASHBOARD.TOP_VENDORS,
);
export const getTopSalesReps = makeThunk(
  "topSalesReps",
  API_URL.DASHBOARD.TOP_SALES_REPS,
);
export const getReorder = makeThunk("reorder", API_URL.DASHBOARD.REORDER);
export const getStockByWarehouse = makeThunk(
  "stockByWarehouse",
  API_URL.DASHBOARD.STOCK_BY_WAREHOUSE,
);
export const getDeadStock = makeThunk("deadStock", API_URL.DASHBOARD.DEAD_STOCK);

export const refreshDashboard = createAsyncThunk(
  "dashboard/refresh",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(API_URL.DASHBOARD.REFRESH);
      return data?.data ?? null;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || "Refresh failed");
    }
  },
);

const WIDGETS = {
  kpis: getKpis,
  salesOrderSummary: getSalesOrderSummary,
  funnel: getFunnel,
  topSellingItems: getTopSellingItems,
  topLocations: getTopLocations,
  topCustomers: getTopCustomers,
  topVendors: getTopVendors,
  topSalesReps: getTopSalesReps,
  reorder: getReorder,
  stockByWarehouse: getStockByWarehouse,
  deadStock: getDeadStock,
};

const initialState = Object.keys(WIDGETS).reduce(
  (acc, key) => {
    acc[key] = { data: null, loading: false, error: null };
    return acc;
  },
  { refreshing: false },
);

const dashboardSlice = createSlice({
  name: "dashboard",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    Object.entries(WIDGETS).forEach(([key, thunk]) => {
      builder
        .addCase(thunk.pending, (state) => {
          state[key].loading = true;
          state[key].error = null;
        })
        .addCase(thunk.fulfilled, (state, action) => {
          state[key].loading = false;
          state[key].data = action.payload;
        })
        .addCase(thunk.rejected, (state, action) => {
          state[key].loading = false;
          state[key].error = action.payload || "Error";
        });
    });
    builder
      .addCase(refreshDashboard.pending, (state) => {
        state.refreshing = true;
      })
      .addCase(refreshDashboard.fulfilled, (state) => {
        state.refreshing = false;
      })
      .addCase(refreshDashboard.rejected, (state) => {
        state.refreshing = false;
      });
  },
});

export default dashboardSlice.reducer;
